import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';

const brand = '#0D9488';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingWorkspace, setLoadingWorkspace] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setError('Please enter your email and password.'); return; }
    setError('');
    setLoading(true);
    const { error: authError } = await signIn(email, password);
    if (authError) { setLoading(false); setError(authError); return; }

    setLoading(false);
    setLoadingWorkspace(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase.from('profiles').select('role').eq('id', user.id).maybeSingle();
      if (profile?.role === 'super_admin') {
        navigate('/super-admin');
        return;
      }
    }
    navigate('/dashboard');
  };

  if (loadingWorkspace) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 gap-5">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: brand }}>
          <i className="ri-plane-line text-white text-2xl" />
        </div>
        <div className="flex flex-col items-center gap-2">
          <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: brand, borderTopColor: 'transparent' }} />
          <p className="text-sm font-medium text-gray-600">Preparing your workspace...</p>
          <p className="text-xs text-gray-400">Loading your agency data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left: illustration */}
      <div
        className="hidden lg:flex flex-col w-[46%] relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, #0D9488 0%, #059669 100%)` }}
      >
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/79e1f2c907c5347c0ad8c6122a4a727b.jpg"
            alt="Travel"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative z-10 flex flex-col h-full p-12">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <i className="ri-plane-line text-white text-xl" />
            </div>
            <div>
              <p className="text-white font-bold text-lg leading-none">Travel Booking</p>
              <p className="text-white/70 text-xs">AI Assistance CRM</p>
            </div>
          </div>

          {/* Main message */}
          <div className="flex-1 flex flex-col justify-center">
            <h2 className="text-4xl font-bold text-white leading-tight mb-4">
              Your Complete<br />Travel Agency<br />Command Center
            </h2>
            <p className="text-white/70 text-base leading-relaxed max-w-sm">
              Manage leads, bookings, invoices, and your entire agency workflow — all in one intelligent platform.
            </p>

            <div className="grid grid-cols-2 gap-3 mt-10">
              {[
                { icon: 'ri-user-star-line', label: 'Multi-tenant CRM' },
                { icon: 'ri-whatsapp-line', label: 'WhatsApp Integrated' },
                { icon: 'ri-robot-2-line', label: 'AI Automation' },
                { icon: 'ri-file-chart-line', label: 'Smart Reporting' },
              ].map((f) => (
                <div key={f.label} className="flex items-center gap-2.5 bg-white/10 rounded-xl px-3.5 py-3">
                  <i className={`${f.icon} text-white text-base`} />
                  <span className="text-white/80 text-sm font-medium">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="flex gap-8">
            {[
              { value: '500+', label: 'Agencies' },
              { value: '50k+', label: 'Bookings' },
              { value: '99.9%', label: 'Uptime' },
            ].map((s) => (
              <div key={s.label}>
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: brand }}>
              <i className="ri-plane-line text-white text-lg" />
            </div>
            <span className="font-bold text-gray-800">Travel Booking AI</span>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-800">Welcome back</h1>
            <p className="text-gray-400 text-sm mt-1">Sign in to your agency dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-400" />
                <p className="text-sm text-red-500">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-teal-400 transition-colors"
                  placeholder="you@youragency.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-teal-400 transition-colors"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className={`${showPass ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded" />
                <span className="text-sm text-gray-600">Remember me</span>
              </label>
              <a href="/forgot-password" className="text-sm cursor-pointer hover:underline" style={{ color: brand }}>
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl text-white font-semibold text-sm cursor-pointer disabled:opacity-70 transition-opacity flex items-center justify-center gap-2 whitespace-nowrap"
              style={{ backgroundColor: brand }}
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <i className="ri-login-box-line" />
                  Sign In to Dashboard
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-400">
              New travel agency?{' '}
              <a href="/register" className="font-semibold cursor-pointer hover:underline" style={{ color: brand }}>
                Register your agency
              </a>
            </p>
          </div>

          {/* Demo hint */}
          <div className="mt-6 bg-teal-50 rounded-xl px-4 py-3 border border-teal-100">
            <p className="text-xs text-teal-600 font-medium mb-1">
              <i className="ri-information-line mr-1" />
              Demo credentials pre-filled
            </p>
            <p className="text-xs text-teal-500">Click &quot;Sign In&quot; to explore the full dashboard</p>
          </div>
        </div>
      </div>
    </div>
  );
}
